import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Calendar, MapPin, Gauge, Thermometer, Ruler, Weight, Shirt, 
  Fish, Star, Waves, Camera, Clock, Eye, User, Building2, FileText, Link2, Send
} from "lucide-react";
import { toast } from "sonner";
import { createEntityLink } from "@/components/EntityEmbed";
import ShareReportModal from "@/components/ShareReportModal";
import { BACKEND_URL } from '@/utils/apiConfig';

// Helper component for info rows - defined outside main component
const InfoRow = ({ icon: Icon, label, value, unit = "" }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="w-5 h-5 text-cyan-600 flex-shrink-0" />
      <span className="text-gray-600">{label}:</span>
      <span className="font-medium">{value}{unit}</span>
    </div>
  );
};

// Helper component for section cards - defined outside main component
const SectionCard = ({ title, icon: Icon, children }) => (
  <Card className="mb-4">
    <CardContent className="pt-4">
      <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
        <Icon className="w-5 h-5 text-cyan-600" />
        {title}
      </h3>
      {children}
    </CardContent>
  </Card>
);

const DiveReportModal = ({ report, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("dive");
  const [showShareModal, setShowShareModal] = useState(false);

  if (!report) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-[10200]" overlayClassName="z-[10199]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Waves className="w-6 h-6 text-cyan-600" />
              {report.title}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                data-testid="share-report-btn"
              >
                <Send className="w-3.5 h-3.5" /> Share
              </button>
              <button
                onClick={() => {
                  const link = createEntityLink("report", report.id);
                  navigator.clipboard.writeText(link);
                  toast.success("Report link copied! Paste it in chat or forum posts.");
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                data-testid="copy-report-link-btn"
              >
                <Link2 className="w-3.5 h-3.5" /> Copy Link
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="w-4 h-4" />
            {formatDate(report.dive_date)}
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dive">
              <Gauge className="w-4 h-4 mr-2" />
              Dive
            </TabsTrigger>
            <TabsTrigger value="site">
              <MapPin className="w-4 h-4 mr-2" />
              Site
            </TabsTrigger>
            <TabsTrigger value="notes">
              <FileText className="w-4 h-4 mr-2" />
              Notes
            </TabsTrigger>
          </TabsList>

          {/* DIVE TAB */}
          <TabsContent value="dive" className="mt-4 space-y-4">
            {/* Dive Metrics */}
            <SectionCard title="Dive Metrics" icon={Ruler}>
              <div className="grid grid-cols-2 gap-2">
                <InfoRow 
                  icon={Ruler} 
                  label="Max Depth" 
                  value={report.depth} 
                  unit={` ${report.depth_unit || 'm'}`} 
                />
                <InfoRow 
                  icon={Clock} 
                  label="Dive Time" 
                  value={report.dive_time} 
                  unit=" min" 
                />
              </div>
            </SectionCard>

            {/* Temperature */}
            {report.temperature && (report.temperature.outside || report.temperature.surface || report.temperature.underwater) && (
              <SectionCard title="Temperature" icon={Thermometer}>
                <div className="grid grid-cols-3 gap-4">
                  {report.temperature.outside && (
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm text-gray-600">Outside</div>
                      <div className="text-xl font-semibold text-blue-600">
                        {report.temperature.outside}°{report.temperature.unit || 'C'}
                      </div>
                    </div>
                  )}
                  {report.temperature.surface && (
                    <div className="text-center p-3 bg-cyan-50 rounded-lg">
                      <div className="text-sm text-gray-600">Surface</div>
                      <div className="text-xl font-semibold text-cyan-600">
                        {report.temperature.surface}°{report.temperature.unit || 'C'}
                      </div>
                    </div>
                  )}
                  {report.temperature.underwater && (
                    <div className="text-center p-3 bg-teal-50 rounded-lg">
                      <div className="text-sm text-gray-600">Underwater</div>
                      <div className="text-xl font-semibold text-teal-600">
                        {report.temperature.underwater}°{report.temperature.unit || 'C'}
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* Equipment */}
            {report.equipment && Object.values(report.equipment).some(v => v) && (
              <SectionCard title="Equipment" icon={Shirt}>
                <div className="space-y-2">
                  {report.equipment.gas_type && (
                    <InfoRow 
                      icon={Gauge} 
                      label="Gas" 
                      value={report.equipment.gas_type === "Nitrox" && report.equipment.nitrox_percentage 
                        ? `Nitrox ${report.equipment.nitrox_percentage}%` 
                        : report.equipment.gas_type} 
                    />
                  )}
                  {report.equipment.cylinder_size && (
                    <InfoRow 
                      icon={Gauge} 
                      label="Cylinder" 
                      value={`${report.equipment.cylinder_size} ${report.equipment.cylinder_type || ''}`} 
                    />
                  )}
                  {(report.equipment.starting_pressure || report.equipment.ending_pressure) && (
                    <InfoRow 
                      icon={Gauge} 
                      label="Air" 
                      value={`${report.equipment.starting_pressure || '?'} → ${report.equipment.ending_pressure || '?'} bar`} 
                    />
                  )}
                  {report.equipment.suit_type && (
                    <InfoRow 
                      icon={Shirt} 
                      label="Suit" 
                      value={`${report.equipment.suit_type}${report.equipment.suit_thickness ? ` ${report.equipment.suit_thickness}mm` : ''}${report.equipment.suit_size ? ` (${report.equipment.suit_size})` : ''}`} 
                    />
                  )}
                  {report.equipment.bcd_type && (
                    <InfoRow 
                      icon={Shirt} 
                      label="BCD" 
                      value={`${report.equipment.bcd_type}${report.equipment.bcd_size ? ` (${report.equipment.bcd_size})` : ''}`} 
                    />
                  )}
                  {report.equipment.hood && (
                    <InfoRow icon={Shirt} label="Hood" value={report.equipment.hood_size || "Yes"} />
                  )}
                  {report.equipment.boots && (
                    <InfoRow icon={Shirt} label="Boots" value={report.equipment.boots_size || "Yes"} />
                  )}
                  {report.equipment.weights && (
                    <InfoRow 
                      icon={Weight} 
                      label="Weights" 
                      value={`${report.equipment.weights} ${report.equipment.weights_unit || 'kg'}${report.equipment.weight_type ? ` (${report.equipment.weight_type})` : ''}`} 
                    />
                  )}
                </div>
              </SectionCard>
            )}

            {/* Dive Profile Photo */}
            {report.dive_profile_photo && (
              <SectionCard title="Dive Profile Photo" icon={Camera}>
                <img 
                  src={`${BACKEND_URL}${report.dive_profile_photo}`}
                  alt="Dive profile"
                  className="w-full max-w-md rounded-lg"
                />
              </SectionCard>
            )}
          </TabsContent>

          {/* SITE TAB */}
          <TabsContent value="site" className="mt-4 space-y-4">
            {/* Location */}
            <SectionCard title="Location" icon={MapPin}>
              <div className="space-y-2">
                {report.country && (
                  <InfoRow icon={MapPin} label="Country" value={report.country} />
                )}
                {report.dive_site_name && (
                  <InfoRow icon={MapPin} label="Dive Site" value={report.dive_site_name} />
                )}
                {report.is_custom_site && report.custom_coordinates?.lat && (
                  <InfoRow 
                    icon={MapPin} 
                    label="Coordinates" 
                    value={`${report.custom_coordinates.lat}, ${report.custom_coordinates.lng}`} 
                  />
                )}
              </div>
            </SectionCard>

            {/* Conditions */}
            {report.conditions && Object.values(report.conditions).some(v => v) && (
              <SectionCard title="Conditions" icon={Waves}>
                <div className="grid grid-cols-2 gap-3">
                  {report.conditions.current && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm text-gray-600">Current</div>
                      <div className="font-medium">{report.conditions.current}</div>
                    </div>
                  )}
                  {report.conditions.waves && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm text-gray-600">Waves</div>
                      <div className="font-medium">{report.conditions.waves}</div>
                    </div>
                  )}
                  {report.conditions.surge && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm text-gray-600">Surge</div>
                      <div className="font-medium">{report.conditions.surge}</div>
                    </div>
                  )}
                  {report.conditions.visibility && (
                    <div className="p-3 bg-cyan-50 rounded-lg">
                      <div className="text-sm text-gray-600">Visibility</div>
                      <div className="font-medium">
                        {report.conditions.visibility} {report.conditions.visibility_unit || 'm'}
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* Marine Life */}
            {report.marine_life_observations?.length > 0 && (
              <SectionCard title="Marine Life Observed" icon={Fish}>
                <div className="flex flex-wrap gap-2">
                  {report.marine_life_observations.map((obs, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm"
                    >
                      {obs}
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Rating */}
            {report.rating && (
              <SectionCard title="Site Rating" icon={Star}>
                {renderStars(report.rating)}
              </SectionCard>
            )}

            {/* Site Photos */}
            {report.site_photos?.length > 0 && (
              <SectionCard title="Site Photos" icon={Camera}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {report.site_photos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img 
                        src={`${BACKEND_URL}${photo.url || photo}`}
                        alt={`Site photo ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      {photo.hide_from_gallery && (
                        <div className="absolute bottom-1 right-1 bg-black/50 rounded px-1 py-0.5">
                          <Eye className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Species ID Request */}
            {report.species_id_requested && report.species_id_photo_urls?.length > 0 && (
              <SectionCard title="Species ID Requested" icon={Fish}>
                <div className="text-sm text-amber-600 mb-2">
                  {report.species_id_photo_urls.length} photo(s) submitted for identification
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {report.species_id_photo_urls.map((url, idx) => (
                    <img 
                      key={idx}
                      src={`${BACKEND_URL}${url}`}
                      alt={`Species ID ${idx + 1}`}
                      className="w-full h-20 object-cover rounded border-2 border-amber-300"
                    />
                  ))}
                </div>
              </SectionCard>
            )}
          </TabsContent>

          {/* NOTES TAB */}
          <TabsContent value="notes" className="mt-4 space-y-4">
            {/* Dive Center */}
            {report.dive_center_name && (
              <SectionCard title="Dive Center" icon={Building2}>
                <div className="text-lg font-medium">{report.dive_center_name}</div>
              </SectionCard>
            )}

            {/* Buddies */}
            {report.buddies?.length > 0 && (
              <SectionCard title="Dive Buddies" icon={User}>
                <div className="flex flex-wrap gap-2">
                  {report.buddies.map((buddy, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {buddy.user_name}
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Notes */}
            {report.notes && (
              <SectionCard title="Notes" icon={FileText}>
                <div className="whitespace-pre-wrap text-gray-700">{report.notes}</div>
              </SectionCard>
            )}

            {/* Show message if no notes data */}
            {!report.dive_center_name && !report.buddies?.length && !report.notes && (
              <div className="text-center text-gray-500 py-8">
                No notes recorded for this dive
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    <ShareReportModal
      reportId={report.id}
      reportTitle={report.title}
      isOpen={showShareModal}
      onClose={() => setShowShareModal(false)}
    />
    </>
  );
};

export default DiveReportModal;
